import {
  Autocomplete,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useFormik } from "formik";
import moment from "moment";
import { IoMdAddCircleOutline } from "react-icons/io";
import { useProSidebar } from "react-pro-sidebar";
import { FaChevronLeft, FaChevronRight, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  getCustomerDetail,
  getCustomerList,
  getHistoryInvoice,
  getPPNStatus,
  setNewInvoice,
} from "../../../services/invoiceApi";
import { useEffect, useState } from "react";
import { getSipaAndFullname, getUserProfile } from "../../../services/authApi";
import ModalAddProductInvoice from "./modal/ModalAddProductInvoice";
import PopupSingleCalendar from "../../common/particles.jsx/PopupSingleCalendar";

const FETCH_LIMIT = 10;

const toIDR = (val) =>
  val?.toLocaleString("id-ID", {
    style: "currency",
    currency: "IDR",
  });

const CreateInvoice = ({ isWarehouse }) => {
  const navigate = useNavigate();
  const { broken } = useProSidebar();
  const [searchCustomer, setSearchCustomer] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [offsetHistory, setOffsetHistory] = useState(1);
  const [openCalendar, setOpenCalendar] = useState(false);

  const generateRandomString = () => {
    const length = 8;
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += characters.charAt(
        Math.floor(Math.random() * characters.length)
      );
    }
    return result;
  };

  const getSubtotal = () => {
    return formik.values.product.length > 0
      ? formik.values.product.reduce(
          (acc, num) => acc + getPriceAfterDiscount(num),
          0
        )
      : 0;
  };

  const getDppPrice = () => {
    return (
      (formik.values.product.length > 0 ? getSubtotal() : 0) *
      (dataPpn &&
      dataPpn.ppn_value &&
      dataPpn.ppn_value !== 0 &&
      dataPpn.ppn_value * 100 === 12
        ? 11 / 12
        : 1)
    );
  };

  const getPPnPrice = () => {
    return (
      getDppPrice() * (dataPpn && dataPpn.ppn_value ? dataPpn.ppn_value : 0)
    );
  };

  const getGrandTotal = () => {
    return getSubtotal() + getPPnPrice() - formik.values.voucher;
  };

  const findPrimaryStockNew = (oldUom, item) => {
    const product =
      formik.values.product[
        formik.values.product.findIndex(
          (prod) => prod.product_id === item.product_id
        )
      ];

    if (product) {
      return oldUom === 0
        ? product.quantity
        : product.quantity / product.product_units.total_primary;
    }
  };

  const findPrimaryStock = (item) => {
    const product =
      formik.values.product[
        formik.values.product.findIndex(
          (prod) => prod.product_id === item.product_id
        )
      ];

    if (product) {
      return item.type_uom === 0
        ? product.quantity
        : product.quantity / product.product_units.total_secondary;
    }

    return 0;
  };

  const findMinimumGrosir = (item) => {
    if (item.length === 0) return;

    const min = Math.min(...item.price_grosir.map((price) => price.minimum));
    return item.price_grosir.find((price) => price.minimum === min);
  };

  const findMaximumGrosir = (item) => {
    if (item.length === 0) return;

    const max = Math.max(...item.price_grosir.map((price) => price.maximum));
    return item.price_grosir.find((price) => price.maximum === max);
  };

  const findGrosirPrice = (item) => {
    const price = item.price_grosir.find((price) => {
      return (
        price.minimum <= findPrimaryStock(item) &&
        findPrimaryStock(item) <= price.maximum
      );
    });

    return price ? price.price_grosirs : 0;
  };

  const getPriceAfterDiscount = (item) => {
    var total = 0;
    var basePrice = item.quantity * item.selected_price;
    const discArr = [item.discount, ...item.additional_discount];

    discArr.map((disc) => {
      total = basePrice * ((100 - disc) / 100);
      basePrice = total;
    });

    return total;
  };

  const getTotalDiscountAdditional = (order) => {
    return formik.values.product.length > 0
      ? formik.values.product.reduce(
          (acc, num) =>
            acc +
            num.quantity *
              num.selected_price *
              ((100 - num.discount) / 100) *
              (order === 0
                ? (num.additional_discount?.[0] ?? 0) / 100
                : ((100 - (num.additional_discount?.[0] ?? 0)) / 100) *
                  ((num.additional_discount?.[1] ?? 0) / 100)),
          0
        )
      : 0;
  };

  const addAdditionalDiscount = (i, discIndex) => {
    formik.setValues({
      ...formik.values,
      product: [
        ...formik.values.product.slice(0, i),
        {
          ...formik.values.product[i],
          additional_discount:
            discIndex === 0
              ? [0]
              : [...formik.values.product[i].additional_discount, 0],
        },
        ...formik.values.product.slice(i + 1),
      ],
    });
  };

  const handleAdditionalDisc = (i, discIndex, newVal) => {
    var tempAdditionalDiscount = [
      ...formik.values.product[i].additional_discount,
    ];
    tempAdditionalDiscount[discIndex] =
      Number(newVal) > 100 ? 100 : Number(newVal);

    formik.setValues({
      ...formik.values,
      product: [
        ...formik.values.product.slice(0, i),
        {
          ...formik.values.product[i],
          additional_discount: tempAdditionalDiscount,
        },
        ...formik.values.product.slice(i + 1),
      ],
    });
  };

  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => getUserProfile(),
  });

  const { data: dataCustomer, isFetching } = useQuery({
    queryKey: ["customer-list", searchCustomer],
    queryFn: () =>
      getCustomerList(
        FETCH_LIMIT,
        1,
        searchCustomer,
        isWarehouse ? isWarehouse : false
      ),
  });

  const { data: dataPpn } = useQuery({
    queryFn: () => getPPNStatus(isWarehouse ? isWarehouse : false),
  });

  const { data: dataDiscountCustomer, isFetching: isFetchingDiscountCustomer } =
    useQuery({
      queryKey: ["customer-detail", selectedCustomer],
      enabled: selectedCustomer !== undefined && selectedCustomer !== null,
      queryFn: () =>
        getCustomerDetail(
          selectedCustomer.partner_id,
          isWarehouse ? isWarehouse : false
        ),
    });

  const { data: sipaAndFullnameData, isFetching: isFetchingSipa } = useQuery({
    queryKey: ["sipa-fullname", profile?.id],
    queryFn: () =>
      getSipaAndFullname(profile && profile !== undefined ? profile.id : 0),
  });

  const formik = useFormik({
    initialValues: {
      invoice_date: moment().format("YYYY-MM-DD"),
      due_date: moment().add(30, "d").format("YYYY-MM-DD"),
      invoice_number:
        "INV/" + moment().format("YYYY") + "/" + generateRandomString(),
      product: [],
      customer_id: 0,
      sales_code: "",
      warehouse: isWarehouse ? isWarehouse : false,
      faktur_number: "",
      voucher: 0,
      dpp: 0,
      additional_disc_1: 0,
      additional_disc_2: 0,
    },
    onSubmit: (values) => {
      mutateNewInvoice({
        ...values,
        product: values.product.map((item) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          batch_number: item.batch_no,
          expired_date: item.batch_exp,
          price: item.selected_price,
          on_us: item.on_us,
          off_us: item.off_us,
          promo: item.promo,
          product_units: item.product_units,
          type_uom: item.type_uom,
          quantity: item.quantity,
          stock: item.stock,
          discount: item.discount,
          additional_discount: item.additional_discount,
        })),
        additional_disc_1: getTotalDiscountAdditional(0),
        additional_disc_2: getTotalDiscountAdditional(1),
      });
    },
  });

  useEffect(() => {
    formik.setFieldValue("dpp", getDppPrice());
  }, [formik.values.product]);

  const { mutate: mutateNewInvoice, isLoading } = useMutation({
    mutationFn: setNewInvoice,
    onSuccess: () =>
      navigate(`${isWarehouse ? "/central-warehouse" : ""}/invoice/invoice`),
  });

  const {
    mutate: mutateHistory,
    isLoading: isLoadingHistory,
    data: dataHistory,
  } = useMutation({
    mutationFn: getHistoryInvoice,
  });

  useEffect(() => {
    var temp = [...formik.values.product];
    temp.map((item, i) => {
      temp[i].discount =
        dataDiscountCustomer && selectedCustomer
          ? dataDiscountCustomer.discount
          : 0;
    });
    formik.setFieldValue(
      "product",
      dataDiscountCustomer ? temp : formik.values.product
    );
  }, [dataDiscountCustomer]);

  useEffect(() => {
    if (selectedCustomer) {
      mutateHistory({
        customer_id: selectedCustomer.partner_id,
        offset: offsetHistory,
        limit: 1,
        warehouse: isWarehouse ? isWarehouse : false,
      });
    }
  }, [selectedCustomer, offsetHistory]);

  return (
    <div className="text-[12px]">
      <div className="flex items-center gap-2 mb-4">
        <DesktopDatePicker
          label="Tanggal Invoice"
          value={
            formik.values.invoice_date === ""
              ? null
              : formik.values.invoice_date
          }
          onChange={(date) =>
            formik.setFieldValue(
              "invoice_date",
              date ? moment(date).format("YYYY-MM-DD") : ""
            )
          }
          inputFormat="DD/MM/YYYY"
          sx={{ width: "100%" }}
          renderInput={(params) => {
            return <TextField fullWidth {...params} />;
          }}
        />
        <TextField
          label="Nomor Faktur Pajak"
          value={formik.values.faktur_number}
          onChange={(e) =>
            formik.setFieldValue("faktur_number", e.target.value)
          }
          fullWidth
        />
        <FormControl sx={{ width: "100%" }}>
          <InputLabel>Jatuh Tempo</InputLabel>
          <Select
            sx={{ width: "100%" }}
            value={formik.values.due_date === "" ? 0 : 2}
            onChange={(e) => {
              formik.setFieldValue("due_date", "");
              setOpenCalendar(e.target.value === 1);
            }}
          >
            <MenuItem value={0}>COD</MenuItem>
            <MenuItem value={1}>Pilih Tanggal</MenuItem>
            <MenuItem value={2} sx={{ display: "none" }}>
              {formik.values.due_date
                ? moment(formik.values.due_date).format("DD/MM/YYYY")
                : ""}
            </MenuItem>
          </Select>
        </FormControl>
        <Autocomplete
          fullWidth
          loading={isFetching}
          disablePortal
          options={
            dataCustomer && dataCustomer.data !== null ? dataCustomer.data : []
          }
          value={selectedCustomer}
          onChange={(_, inputValue) => {
            setOffsetHistory(1);
            setSelectedCustomer(inputValue);
            formik.setFieldValue(
              "customer_id",
              inputValue === null ? 0 : inputValue.partner_id
            );
          }}
          onInputChange={(_, onInputValue, reason) => {
            if (reason === "input") {
              setSearchCustomer(onInputValue);
            }
          }}
          getOptionLabel={(option) => option.partner_name || ""}
          renderOption={(props, option) => {
            return (
              <li {...props} key={option.partner_id}>
                {option.partner_name}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} fullWidth label="Customer" />
          )}
        />
        <TextField
          label="Kode Sales"
          value={formik.values.sales_code}
          onChange={(e) => formik.setFieldValue("sales_code", e.target.value)}
          fullWidth
        />
        <button
          type="button"
          className="border rounded-md flex items-center justify-between gap-2 w-fit py-2.5 px-2 bg-blue-500 text-white hover:bg-gray-300 whitespace-nowrap text-nowrap"
          onClick={() => setOpenAdd(true)}
        >
          Tambah Produk
          <IoMdAddCircleOutline className="text-[18px]" />
        </button>
      </div>

      <div
        className={`mb-4 items-start gap-4 ${
          broken ? "block" : "flex"
        } overflow-x-auto w-full`}
      >
        <div className="w-full h-full">
          <div className="w-full flex gap-2 font-bold mb-4 items-center">
            <p className="w-full">
              Customer Diskon :{"  "}
              {isFetchingDiscountCustomer ? (
                <CircularProgress size={15} />
              ) : dataDiscountCustomer && selectedCustomer ? (
                dataDiscountCustomer.discount
              ) : (
                ""
              )}
            </p>
            <p className="w-full">
              Nama Apoteker : {"  "}
              {isFetchingSipa ? (
                <CircularProgress size={15} />
              ) : sipaAndFullnameData && sipaAndFullnameData !== undefined ? (
                sipaAndFullnameData.fullname
              ) : (
                ""
              )}
            </p>
            <p className="w-full">
              No. SIPA : {"  "}
              {isFetchingSipa ? (
                <CircularProgress size={15} />
              ) : sipaAndFullnameData && sipaAndFullnameData !== undefined ? (
                sipaAndFullnameData.sipa_id
              ) : (
                ""
              )}
            </p>
          </div>

          <div className="flex w-full mb-8 min-h-[40vh]">
            <div className="w-full">
              <div className="block overflow-x-auto w-full">
                <table className="w-full overflow-x-auto rounded-md border-collapse">
                  <thead className="bg-blue-500 text-white">
                    <th className="p-2 text-center">Nama Produk</th>
                    <th className="p-2 text-center">Kuantitas</th>
                    <th className="p-2 text-center">Batch</th>
                    <th className="p-2 text-center">Expired</th>
                    <th className="p-2 text-center">Harga Satuan</th>
                    <th className="p-2 text-center w-[200px]">Diskon</th>
                    <th className="p-2 text-center w-[300px]">
                      Diskon Tambahan
                    </th>
                    <th className="p-2 text-center">Promo</th>
                    <th className="p-2 text-center">Total</th>
                    <th className="p-2 text-center">Total + Diskon</th>
                  </thead>
                  <tbody className="border">
                    {formik.values.product.length > 0 ? (
                      formik.values.product.map((item, i) => {
                        return (
                          <tr
                            key={i}
                            className={`${i === 0 ? "" : "border-t"}`}
                          >
                            <td className="p-2 text-center ">
                              {item.product_name}
                            </td>
                            <td className="p-2 text-center ">
                              {item.quantity}{" "}
                              {item.type_uom === 0
                                ? item.product_units.primary_type
                                : item.product_units.secondary_type}
                            </td>
                            <td className="p-2 text-center ">
                              {item.batch_no}
                            </td>
                            <td className="p-2 text-center ">
                              {moment(item.expired_date).format("DD/MM/YYYY")}
                            </td>
                            <td
                              className={`p-2 text-center ${
                                item.additional_discount?.length > 0
                                  ? "align-top"
                                  : "align-center"
                              }`}
                            >
                              <Select
                                value={item.selected_price}
                                onChange={(e) => {
                                  formik.setValues({
                                    ...formik.values,
                                    product: [
                                      ...formik.values.product.slice(0, i),
                                      {
                                        ...formik.values.product[i],
                                        selected_price: e.target.value,
                                        // if selected price === grosir
                                        //// check qty and initial stock
                                        //// change uom & qty to minimum grosir
                                        type_uom:
                                          item.price_grosir.length > 0 &&
                                          e.target.value ===
                                            item.price_grosir.find(
                                              (price) =>
                                                price.price_grosirs ===
                                                e.target.value
                                            )?.price_grosirs
                                            ? 0
                                            : formik.values.product[i].type_uom,
                                        quantity:
                                          item.price_grosir.length > 0 &&
                                          item.price_grosir.find(
                                            (price) =>
                                              price.price_grosirs ===
                                              e.target.value
                                          )?.price_grosirs
                                            ? findPrimaryStock(item) <
                                              findMinimumGrosir(item).minimum
                                              ? findMinimumGrosir(item).minimum
                                              : findPrimaryStock(item)
                                            : item.quantity,
                                      },
                                      ...formik.values.product.slice(i + 1),
                                    ],
                                  });
                                }}
                                renderValue={(selected) => (
                                  <p>
                                    {selected === 0
                                      ? "Pilih harga satuan"
                                      : toIDR(selected)}
                                  </p>
                                )}
                                sx={{ width: "100%" }}
                              >
                                <MenuItem value={0} disabled>
                                  Pilih harga satuan
                                </MenuItem>
                                <MenuItem
                                  value={
                                    item.type_uom === 0
                                      ? item.pharmacy_net_price
                                      : item.pharmacy_net_price /
                                        item.product_units.total_secondary
                                  }
                                >
                                  HNA -{" "}
                                  {toIDR(
                                    item.type_uom === 0
                                      ? item.pharmacy_net_price
                                      : item.pharmacy_net_price /
                                          item.product_units.total_secondary
                                  )}
                                </MenuItem>
                                <MenuItem
                                  value={
                                    item.type_uom === 0
                                      ? item.product_units.primary_sell_price
                                      : item.product_units.secondary_sell_price
                                  }
                                >
                                  Harga Jual -{" "}
                                  {toIDR(
                                    item.type_uom === 0
                                      ? item.product_units.primary_sell_price
                                      : item.product_units.secondary_sell_price
                                  )}
                                </MenuItem>
                                {/* 
                                Cases:
                                1. Stock < min grosir -> show, disable
                                2. Stock >= min grosir -> show, enable
                                   If current qty < min grosir -> change to min grosir & uom primer
                                */}
                                {item.price_grosir.length > 0 ? (
                                  <MenuItem
                                    value={
                                      findPrimaryStock(item) <
                                      findMinimumGrosir(item).minimum
                                        ? findMinimumGrosir(item).price_grosirs
                                        : findPrimaryStock(item) >=
                                            findMaximumGrosir(item).maximum
                                          ? findMaximumGrosir(item)
                                              .price_grosirs
                                          : findGrosirPrice(item)
                                    }
                                    disabled={
                                      item.initial_stock <
                                      findMinimumGrosir(item).minimum
                                    }
                                  >
                                    <div>
                                      <p>
                                        Harga Grosir -{" "}
                                        {toIDR(
                                          findPrimaryStock(item) <
                                            findMinimumGrosir(item).minimum
                                            ? findMinimumGrosir(item)
                                                .price_grosirs
                                            : findPrimaryStock(item) >=
                                                findMaximumGrosir(item).maximum
                                              ? findMaximumGrosir(item)
                                                  .price_grosirs
                                              : findGrosirPrice(item)
                                        )}
                                      </p>
                                      {item.initial_stock <
                                      findMinimumGrosir(item).minimum ? (
                                        <p className="text-red-500 text-[10px]">
                                          Stock tidak mencukupi
                                        </p>
                                      ) : null}
                                    </div>
                                  </MenuItem>
                                ) : item.price_grosir.length === 0 ? (
                                  <MenuItem disabled>
                                    Tidak ada harga grosir
                                  </MenuItem>
                                ) : null}
                              </Select>
                            </td>
                            <td
                              className={`p-2 text-center ${
                                item.additional_discount?.length > 0
                                  ? "align-top"
                                  : "align-center"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <TextField
                                  fullWidth
                                  type="number"
                                  onFocus={(e) =>
                                    e.target.addEventListener(
                                      "wheel",
                                      function (e) {
                                        e.preventDefault();
                                      },
                                      { passive: false }
                                    )
                                  }
                                  value={item.discount.toString()}
                                  sx={{ minWidth: "100px" }}
                                  onChange={(e) => {
                                    if (!isNaN(Number(e.target.value))) {
                                      formik.setValues({
                                        ...formik.values,
                                        product: [
                                          ...formik.values.product.slice(0, i),
                                          {
                                            ...formik.values.product[i],
                                            discount:
                                              Number(e.target.value) > 100
                                                ? 100
                                                : Number(e.target.value),
                                          },
                                          ...formik.values.product.slice(i + 1),
                                        ],
                                      });
                                    }
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        %
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </div>
                            </td>
                            <td className="p-2 text-center ">
                              {item.additional_discount ? (
                                item.additional_discount.length === 0 ? (
                                  <button
                                    type="button"
                                    className="border-2 border-blue-500 text-blue-500 hover:bg-gray-100 rounded-md p-2 whitespace-nowrap"
                                    onClick={() => addAdditionalDiscount(i, 0)}
                                  >
                                    + Tambah Diskon
                                  </button>
                                ) : (
                                  <div>
                                    <div className="flex items-center gap-2">
                                      {item.additional_discount.map(
                                        (disc, discIndex) => {
                                          return (
                                            <>
                                              <TextField
                                                value={disc.toString()}
                                                type="number"
                                                onFocus={(e) =>
                                                  e.target.addEventListener(
                                                    "wheel",
                                                    function (e) {
                                                      e.preventDefault();
                                                    },
                                                    { passive: false }
                                                  )
                                                }
                                                onChange={(e) => {
                                                  if (
                                                    !isNaN(
                                                      Number(e.target.value)
                                                    )
                                                  ) {
                                                    handleAdditionalDisc(
                                                      i,
                                                      discIndex,
                                                      e.target.value
                                                    );
                                                  }
                                                }}
                                                sx={{ width: "100px" }}
                                                InputProps={{
                                                  endAdornment: (
                                                    <InputAdornment>
                                                      %
                                                    </InputAdornment>
                                                  ),
                                                }}
                                              />
                                              <div
                                                className="w-[24px] h-[24px] bg-red-500 text-white rounded-md p-[6px] cursor-pointer hover:bg-gray-300"
                                                onClick={() => {
                                                  formik.setFieldValue(
                                                    `product[${i}].additional_discount`,
                                                    item.additional_discount
                                                      .length === 1
                                                      ? []
                                                      : [
                                                          formik.values.product[
                                                            i
                                                          ].additional_discount[
                                                            discIndex === 0
                                                              ? 1
                                                              : 0
                                                          ],
                                                        ]
                                                  );
                                                }}
                                              >
                                                <FaTrash className="w-full h-full" />
                                              </div>

                                              {item.additional_discount.length >
                                                1 && discIndex === 0 ? (
                                                <p className="text-[18px]">+</p>
                                              ) : null}
                                            </>
                                          );
                                        }
                                      )}
                                    </div>
                                    {item.additional_discount.length === 1 ? (
                                      <button
                                        type="button"
                                        className="border-2 border-blue-500 text-blue-500 hover:bg-gray-100 rounded-md p-1 whitespace-nowrap text-[10px] w-full mt-2 disabled:bg-gray-300"
                                        disabled={
                                          item.additional_discount[0] === 0
                                        }
                                        onClick={() =>
                                          addAdditionalDiscount(i, 1)
                                        }
                                      >
                                        + Tambah
                                      </button>
                                    ) : null}
                                  </div>
                                )
                              ) : null}
                            </td>
                            <td className="p-2 text-center ">
                              {item.promo === "" ? "-" : item.promo}
                            </td>
                            <td className="p-2 text-center ">
                              {toIDR(item.quantity * item.selected_price)}
                            </td>
                            <td className="p-2 text-center ">
                              {toIDR(getPriceAfterDiscount(item))}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td className="p-2 text-center" colSpan={10}>
                          Tidak ada produk
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {formik.values.product.length > 0 ? (
            <div className="w-full flex items-end justify-end mb-8">
              <div className="w-1/2">
                <div className="flex gap-2 items-center mb-2">
                  <p className="w-1/2">Jumlah</p>
                  <p>:</p>
                  <p className="w-full">
                    {toIDR(
                      formik.values.product.length > 0
                        ? formik.values.product.reduce(
                            (acc, num) =>
                              acc + num.quantity * num.selected_price,
                            // ((100 - num.discount) / 100),
                            0
                          )
                        : 0
                    )}
                  </p>
                </div>
                <div className="flex gap-2 items-center mb-2">
                  <p className="w-1/2">Diskon</p>
                  <p>:</p>
                  <p className="w-full">
                    {toIDR(
                      formik.values.product.length > 0
                        ? formik.values.product.reduce(
                            (acc, num) =>
                              acc +
                              num.quantity *
                                num.selected_price *
                                (num.discount / 100),
                            0
                          )
                        : 0
                    )}
                  </p>
                </div>
                <div className="flex gap-2 items-center mb-2">
                  <p className="w-1/2">Diskon Tambahan 1</p>
                  <p>:</p>
                  <p className="w-full">
                    {toIDR(getTotalDiscountAdditional(0))}
                  </p>
                </div>
                <div className="flex gap-2 items-center mb-2">
                  <p className="w-1/2">Diskon Tambahan 2</p>
                  <p>:</p>
                  <p className="w-full">
                    {toIDR(getTotalDiscountAdditional(1))}
                  </p>
                </div>
                <div className="flex gap-2 items-center mb-2">
                  <p className="w-1/2">Subtotal</p>
                  <p>:</p>
                  <p className="w-full">
                    {toIDR(
                      formik.values.product.length > 0 ? getSubtotal() : 0
                    )}
                  </p>
                </div>
                <div className="flex gap-2 items-center mb-2">
                  <p className="w-1/2">DPP</p>
                  <p>:</p>
                  <p className="w-full">{toIDR(getDppPrice())}</p>
                </div>
                <div className="flex gap-2 items-center mb-2">
                  <p className="w-1/2">PPN</p>
                  <p>:</p>
                  <p className="w-full">{toIDR(getPPnPrice())}</p>
                </div>{" "}
                <div className="flex gap-2 items-center mb-2">
                  <p className="w-1/2">Voucher</p>
                  <p>:</p>
                  <TextField
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">Rp</InputAdornment>
                      ),
                    }}
                    type="number"
                    onFocus={(e) =>
                      e.target.addEventListener(
                        "wheel",
                        function (e) {
                          e.preventDefault();
                        },
                        { passive: false }
                      )
                    }
                    value={formik.values.voucher.toString()}
                    onChange={(e) => {
                      if (!isNaN(Number(e.target.value))) {
                        formik.setValues({
                          ...formik.values,
                          voucher: Number(e.target.value),
                        });
                      }
                    }}
                  />
                </div>
                <div className="flex gap-2 items-center mb-2 bg-blue-100 text-blue-500 p-1 rounded-md font-bold">
                  <p className="w-1/2">Jumlah Bayar</p>
                  <p>:</p>
                  <p className="w-full">
                    {toIDR(getGrandTotal() < 0 ? 0 : getGrandTotal())}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
          <div className="w-full flex items-end justify-end mb-4">
            <div className="w-1/2 flex gap-4 items-center">
              <button
                type="button"
                className="w-full p-2 rounded-md border-2 border-black hover:bg-gray-300"
                onClick={() => navigate(`/invoice/invoice`)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="w-full p-2 rounded-md border-2 border-blue-500 bg-blue-500 text-white hover:bg-gray-300 hover:border-gray-300 disabled:bg-gray-300 disabled:border-none"
                onClick={() => formik.handleSubmit()}
                disabled={
                  formik.values.product.length === 0 ||
                  formik.values.customer_id === 0 ||
                  isLoading ||
                  !formik.values.customer_id ||
                  !formik.values.product.every(
                    (item) =>
                      item.selected_price > 0 &&
                      item.additional_discount.every((disc) => disc > 0)
                  )
                }
              >
                {isLoading ? <CircularProgress size={10} /> : "Save"}
              </button>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-1/2">
          <div className="border-2 rounded-md p-3 h-full">
            <p className="font-bold mb-2 text-[14px]">
              Riwayat Faktur Pelanggan
            </p>
            <div className="flex justify-end w-full mb-2">
              <div className="flex gap-4 justify-between items-center w-3/4">
                <button
                  type="button"
                  className="border rounded-md p-1 disabled:bg-gray-300 disabled:text-white w-[32px] h-[32px] flex items-center justify-center"
                  disabled={
                    offsetHistory === 1 || !dataHistory || isLoadingHistory
                  }
                  onClick={() => setOffsetHistory(offsetHistory - 1)}
                >
                  <FaChevronLeft />
                </button>
                <p>
                  {selectedCustomer && dataHistory && dataHistory.data ? (
                    <>
                      {moment(dataHistory.data[0]?.history_date).format(
                        "DD MMMM YYYY"
                      )}
                    </>
                  ) : (
                    "-"
                  )}
                </p>
                <button
                  type="button"
                  className="border rounded-md p-1 disabled:bg-gray-300 disabled:text-white w-[32px] h-[32px] flex items-center justify-center"
                  disabled={
                    (dataHistory && offsetHistory === dataHistory.count) ||
                    !dataHistory ||
                    isLoadingHistory
                  }
                  onClick={() => setOffsetHistory(offsetHistory + 1)}
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
            <hr className="my-2" />
            {selectedCustomer &&
            dataHistory &&
            !isLoadingHistory &&
            dataHistory.data &&
            dataHistory.count > 0 ? (
              <>
                <div className="mb-2">
                  <div className="w-full flex gap-2 items-center">
                    <p className="w-1/3">No. Faktur</p>
                    <p>:</p>
                    <p>{dataHistory.data[0]?.faktur_number}</p>
                  </div>
                  <div className="w-full flex gap-2 items-center">
                    <p className="w-1/3">Total Bayar</p>
                    <p>:</p>
                    <p>{toIDR(dataHistory.data[0]?.total_payment)}</p>
                  </div>
                  <div className="w-full flex gap-2 items-center">
                    <p className="w-1/3">Jatuh Tempo</p>
                    <p>:</p>
                    <p>
                      {dataHistory.data[0]?.due_date === ""
                        ? "-"
                        : moment(dataHistory.data[0]?.due_date).format(
                            "DD-MM-YYYY"
                          )}
                    </p>
                  </div>
                  <div className="w-full flex gap-2 items-center">
                    <p className="w-1/3">Tanggal Pembayaran</p>
                    <p>:</p>
                    <p>
                      {dataHistory.data[0]?.payment_date === ""
                        ? "-"
                        : moment(dataHistory.data[0]?.payment_date).format(
                            "DD-MM-YYYY"
                          )}
                    </p>
                  </div>
                  <div className="w-full flex gap-2 items-center">
                    <p className="w-1/3">Metode Pembayaran</p>
                    <p>:</p>
                    <p>{dataHistory.data[0]?.payment_method}</p>
                  </div>
                  <div className="w-full flex gap-2 items-center">
                    <p className="w-1/3">Status</p>
                    <p>:</p>
                    <p>
                      {dataHistory.data[0]?.status === 1
                        ? "Belum Dibayar"
                        : dataHistory.data[0]?.status === 2
                          ? "Belum Lunas"
                          : dataHistory.data[0]?.status === 3
                            ? "Lunas"
                            : "-"}
                    </p>
                  </div>
                </div>

                <div className="max-h-[50vh] overflow-y-auto w-full bg-gray-100 rounded-md p-3 mb-4">
                  {dataHistory.data[0]?.product.map((item, i) => (
                    <div
                      className={`bg-white rounded-md py-2 px-4 ${
                        i === dataHistory.data[0]?.product.length - 1
                          ? ""
                          : "mb-3"
                      }`}
                      key={i}
                    >
                      <div className="mb-2">
                        <div className="w-full flex gap-2">
                          <p className="w-full">Nama Produk</p>
                          <p className="w-full">Total</p>
                        </div>
                        <div className="w-full flex gap-2 text-[14px] font-bold">
                          <p className="w-full">{item.product_name}</p>
                          <p className="w-full">{toIDR(item.sub_price)}</p>
                        </div>
                      </div>
                      <div className="mb-2">
                        <div className="w-full flex gap-2">
                          <p className="w-full">Kuantitas</p>
                          <p className="w-full">Diskon</p>
                        </div>
                        <div className="w-full flex gap-2 text-[14px] font-bold">
                          <p className="w-full">{item.quantity}</p>
                          <p className="w-full">
                            {" "}
                            {(item.additional_discount
                              ? [item.discount, ...item.additional_discount]
                              : [item.discount]
                            ).map(
                              (disc, discIndex) =>
                                `${disc}% ${
                                  (discIndex === 0 &&
                                    item.additional_discount) ||
                                  (item.additional_discount &&
                                    item.additional_discount.length > 1 &&
                                    discIndex === 1)
                                    ? " + "
                                    : ""
                                }`
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="mb-2">
                        <div className="w-full flex gap-2">
                          <p className="w-full">Harga Satuan</p>
                          <p className="w-full">Total + Diskon</p>
                        </div>
                        <div className="w-full flex gap-2 text-[14px] font-bold">
                          <p className="w-full">{toIDR(item.unit_price)}</p>
                          <p className="w-full">{toIDR(item.total_price)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="w-full flex justify-end">
                  <div className="w-2/3">
                    <div className="w-full flex gap-2 items-center">
                      <p className="w-1/2">Jumlah</p>
                      <p>:</p>
                      <p>{toIDR(dataHistory.data[0]?.total_amount)}</p>
                    </div>
                    <div className="w-full flex gap-2 items-center">
                      <p className="w-1/2">Diskon</p>
                      <p>:</p>
                      <p>{toIDR(dataHistory.data[0]?.total_discount)}</p>
                    </div>
                    <div className="w-full flex gap-2 items-center">
                      <p className="w-1/2">Diskon Tambahan 1</p>
                      <p>:</p>
                      <p>
                        {toIDR(
                          dataHistory.data[0]?.additional_disc_1
                            ? dataHistory.data[0]?.additional_disc_1
                            : 0
                        )}
                      </p>
                    </div>
                    <div className="w-full flex gap-2 items-center">
                      <p className="w-1/2">Diskon Tambahan 2</p>
                      <p>:</p>
                      <p>
                        {toIDR(
                          dataHistory.data[0]?.additional_disc_2
                            ? dataHistory.data[0]?.additional_disc_2
                            : 0
                        )}
                      </p>
                    </div>
                    <div className="w-full flex gap-2 items-center">
                      <p className="w-1/2">Subtotal</p>
                      <p>:</p>
                      <p>{toIDR(dataHistory.data[0]?.sub_total)}</p>
                    </div>
                    <div className="w-full flex gap-2 items-center">
                      <p className="w-1/2">DPP</p>
                      <p>:</p>
                      <p>
                        {toIDR(
                          dataHistory.data[0]?.dpp
                            ? dataHistory.data[0]?.dpp
                            : dataHistory.data[0]?.sub_total
                        )}
                      </p>
                    </div>
                    <div className="w-full flex gap-2 items-center">
                      <p className="w-1/2">PPN</p>
                      <p>:</p>
                      <p>{toIDR(dataHistory.data[0]?.ppn)}</p>
                    </div>
                    <div className="w-full flex gap-2 items-center">
                      <p className="w-1/2">Voucher</p>
                      <p>:</p>
                      <p>{toIDR(dataHistory.data[0]?.voucher)}</p>
                    </div>
                    <div className="w-full flex gap-2 items-center font-bold">
                      <p className="w-1/2">Jumlah Bayar</p>
                      <p>:</p>
                      <p>{toIDR(dataHistory.data[0]?.total)}</p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="h-full min-h-[55vh] flex items-center justify-center text-gray-400 text-center w-full">
                {isLoadingHistory ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    Silahkan isi form tambah faktur <br />
                    terlebih dahulu untuk melihat riwayat
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ModalAddProductInvoice
        open={openAdd}
        setOpen={setOpenAdd}
        formik={formik}
        discountCustomer={
          dataDiscountCustomer && selectedCustomer
            ? dataDiscountCustomer.discount
            : 0
        }
        isWarehouse={isWarehouse}
      />

      <PopupSingleCalendar
        open={openCalendar}
        setOpen={setOpenCalendar}
        handleClose={() => {
          setOpenCalendar(false);
        }}
        value={formik.values.due_date === "" ? null : formik.values.due_date}
        handleChange={(e) => {
          formik.setFieldValue(
            "due_date",
            e ? moment(e).format("YYYY-MM-DD") : ""
          );
        }}
      />
    </div>
  );
};

export default CreateInvoice;
